import { CheckCircle2, Lock } from "lucide-react";
import type { ReactNode } from "react";
import { useImmer } from "use-immer";

import { IgCodeEditor } from "@intelligems/ig-theme/IgCodeEditor";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgStatusCircle } from "@intelligems/ig-theme/IgStatusCircle";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack, useTheme } from "@mui/material";

import { useAgenticLogicTemplateSource } from "@src/api/agentic-logic/get-agentic-logic-template-source";
import { WorkspaceFileList } from "@src/features/agenticCodegen/workspace/components/03-EditorPanel/components/WorkspaceFileList";

import { TemplatePublishStatus } from "./TemplatePublishStatus";
import {
  TEMPLATE_CONFIG_FILE,
  templateWorkspaceDocument,
  templateWorkspaceFiles,
} from "./templateWorkspaceFiles";
import type { TemplateRuleWorkspace } from "./useTemplateRuleWorkspace";

interface TemplateConfigWorkspaceProps {
  editor: TemplateRuleWorkspace;
  descriptionEditor?: ReactNode;
  /**
   * The org's Advanced verdict, threaded down to the publish status alert.
   * A prop rather than a hook so this body stays renderable without a redux
   * provider — it is exercised directly by several specs.
   */
  advancedEnabled: boolean;
}

/**
 * The template-rule editor body, in the same shape as the code workspace's
 * Advanced tab: a file tree on the left, the editor beside it, and the settings
 * rail on the right.
 *
 * The tree carries one editable file — the configuration — over the template's
 * own read-only implementation source, so an author can read the code that will
 * run their configuration and see exactly what it expects. Chrome-free by
 * design: the page frame above (rule detail page or the create panel) owns the
 * name, the description and the write actions.
 */
export const TemplateConfigWorkspace = ({
  editor,
  descriptionEditor,
  advancedEnabled,
}: TemplateConfigWorkspaceProps) => {
  const theme = useTheme();
  const { workspace, template } = editor;
  const sourceQuery = useAgenticLogicTemplateSource(workspace.templateId);
  const [activeFile, setActiveFile] = useImmer(TEMPLATE_CONFIG_FILE);

  const sourceFiles = sourceQuery.data?.files ?? [];
  const files = templateWorkspaceFiles(sourceFiles);
  const openFile = templateWorkspaceDocument({
    activeFile,
    configSource: workspace.source,
    sourceFiles,
  });
  const lineCount = openFile.contents.split("\n").length;

  return (
    <IgPaper
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        p: 0,
      }}
    >
      <Stack sx={{ height: "100%", minHeight: 0 }}>
        {editor.publishJob ? (
          <Stack sx={{ px: 2, pt: 2 }}>
            <TemplatePublishStatus
              job={editor.publishJob}
              isSessionRun={editor.isSessionRun}
              advancedEnabled={advancedEnabled}
            />
          </Stack>
        ) : null}

        <Stack
          direction={{ xs: "column", lg: "row" }}
          sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <Stack sx={{ flex: 1, minHeight: 360, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                borderBottom: `1px solid ${theme.igBorders.core.default}`,
                minHeight: 36,
                px: 1.5,
              }}
            >
              <IgTypography variant="body2" fontWeight={600} noWrap>
                {openFile.name}
              </IgTypography>
              {openFile.isReadOnly ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ ml: "auto" }}
                >
                  <Lock size={13} />
                  <IgTypography variant="caption" color="text.tertiary">
                    Read-only
                  </IgTypography>
                </Stack>
              ) : workspace.isDirty ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ ml: "auto" }}
                >
                  <IgStatusCircle color="warning" />
                  <IgTypography
                    variant="caption"
                    color={theme.palette.warning.main}
                  >
                    Unsaved changes
                  </IgTypography>
                </Stack>
              ) : null}
            </Stack>

            <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
              {/* The tree renders from the first frame with the one file that
                  is always there; the template's source rows join it when the
                  read lands, so the configuration is never behind a spinner. */}
              <WorkspaceFileList
                files={files}
                activeFile={openFile.name}
                onSelect={setActiveFile}
              />
              <Stack
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  overflow: "hidden",
                  "& .cm-editor": {
                    border: "none !important",
                    borderRadius: "0 !important",
                  },
                }}
              >
                <IgCodeEditor
                  // A fresh editor per file: without the key CodeMirror keeps
                  // one file's undo history alive under the next file's text.
                  key={openFile.name}
                  aria-label={
                    openFile.language === "json"
                      ? "Template configuration JSON"
                      : "Template implementation source"
                  }
                  language={openFile.language}
                  value={openFile.contents}
                  onChange={(value) => {
                    if (!openFile.isReadOnly) {
                      workspace.setSource(value);
                    }
                  }}
                  readOnly={openFile.isReadOnly}
                  lint={openFile.language === "json"}
                  copy={openFile.isReadOnly}
                  lineWrapping={false}
                  // `height` sizes .cm-editor itself, which is what gives the
                  // scroller a bounded box; a height in `style` only sizes the
                  // wrapper and leaves a long file clipped by the panel.
                  height="100%"
                  style={{ flex: 1, minHeight: 0 }}
                />
              </Stack>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              sx={{
                bgcolor: theme.igBackgroundColors.baseLightHigh,
                borderTop: `1px solid ${theme.igBorders.core.default}`,
                minHeight: 30,
                px: 1.5,
              }}
            >
              <IgTypography variant="caption" color="text.tertiary">
                {openFile.language === "json" ? "JSON" : "TypeScript"} ·{" "}
                {lineCount} {lineCount === 1 ? "line" : "lines"}
              </IgTypography>
              <IgTypography
                variant="caption"
                color="text.tertiary"
                sx={{ ml: "auto" }}
              >
                {openFile.isReadOnly
                  ? "This first-party code runs your saved configuration"
                  : `Template v${template.templateVersion}`}
              </IgTypography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </IgPaper>
  );
};
